import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CONTACT } from "@/data";
import { useI18n } from "@/i18n";

export const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onHome = pathname === "/" || pathname === "/en" || pathname === "/en/";
  const switchLang = (l) => {
    setLang(l);
    setOpen(false);
    if (onHome) navigate(l === "en" ? "/en/" : "/");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const LINKS = [
    { label: t.nav.approche, href: "#approche", key: "approche" },
    { label: t.nav.expertise, href: "#expertise", key: "expertise" },
    { label: t.nav.projets, href: "#projets", key: "projets" },
    { label: t.nav.profil, href: "#profil", key: "profil" },
    { label: t.nav.contact, href: "#contact", key: "contact" },
  ];

  const LangSwitch = ({ mobile }) => (
    <div
      data-testid="lang-switch"
      className={`flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em] ${mobile ? "" : ""}`}
    >
      {["fr", "en"].map((l, i) => (
        <span key={l} className="flex items-center gap-2">
          {i === 1 && <span className="text-[#c4c4bd]">/</span>}
          <button
            data-testid={`lang-${l}`}
            onClick={() => switchLang(l)}
            className={`px-1 py-2 transition-colors ${lang === l ? "text-[#1c1c1a]" : "text-[#a5a59e] hover:text-[#5c5c57]"}`}
          >
            {l.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );

  return (
    <>
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled && !open ? "bg-[#f4f4f0]/80 backdrop-blur-xl border-b border-[#dcdcd6]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
        <a href="#top" data-testid="nav-logo" onClick={() => setOpen(false)} className="relative z-50 font-display text-2xl tracking-tight text-[#1c1c1a] md:text-3xl">
          Bardine <span className="text-[#9e8d7b]">PA.</span>
        </a>

        <ul className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <li key={l.key}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="link-underline font-body text-[11px] uppercase tracking-[0.22em] text-[#5c5c57] hover:text-[#1c1c1a]"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:block">
          <LangSwitch />
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="relative z-50 -mr-2 px-2 py-3 font-body text-[11px] uppercase tracking-[0.22em] md:hidden"
        >
          {open ? "×" : "Menu"}
        </button>
      </nav>

    </header>

    {open && (
      <div
        data-testid="nav-mobile-menu"
        className="mobile-menu-panel fixed inset-0 z-40 flex flex-col bg-[#f4f4f0] px-6 pb-10 pt-24 md:hidden"
      >
        <ul className="flex flex-1 flex-col justify-center">
          {LINKS.map((l, i) => (
            <li
              key={l.key}
              className="mobile-menu-item border-b border-[#dcdcd6]"
              style={{ animationDelay: `${0.08 + i * 0.06}s` }}
            >
              <a
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.key}`}
                className="flex items-baseline justify-between py-4 font-display text-3xl text-[#1c1c1a]"
              >
                {l.label}
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[#9e8d7b]">
                  0{i + 1}
                </span>
              </a>
            </li>
          ))}
        </ul>
        <div className="mobile-menu-item flex items-end justify-between gap-4" style={{ animationDelay: "0.42s" }}>
          <LangSwitch mobile />
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="nav-mobile-email"
            className="link-underline font-body text-[11px] uppercase tracking-[0.18em] text-[#5c5c57]"
          >
            {CONTACT.email}
          </a>
        </div>
      </div>
    )}
    </>
  );
};
